import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { NetworkError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import type { Sync } from 'modules/integrations_silae_api/lib/IncidenceSync/store'
import IncidenceSync, { Status } from 'modules/integrations_silae_api/lib/IncidenceSync/store'
import authService, { Status as AuthStatus } from 'modules/integrations_silae_api/lib/authService'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'
import CompanyInstallationsChecker from 'modules/payroll_integrations_kombo/lib/CompanyInstallations/checker'

import Errored from './Errored'
import Hiring from './Hiring'
import Leave from './Leave'
import NotAuthenticated from './NotAuthenticated'
import PersonalData from './PersonalData'

interface Props {
  incidenceType: keyof typeof INCIDENCE_TYPES
  incidenceDetail: IncidenceDetail
  companyId?: number
}

const selectComponent = (type: keyof typeof INCIDENCE_TYPES | null) => {
  switch (type) {
    case INCIDENCE_TYPES.hiring:
      return Hiring
    case INCIDENCE_TYPES.name:
    case INCIDENCE_TYPES.address:
    case INCIDENCE_TYPES.gender:
      return PersonalData
    case INCIDENCE_TYPES.sick:
    case INCIDENCE_TYPES.parental:
    case INCIDENCE_TYPES.custom_leave:
      return Leave
    default:
      return null
  }
}

const Incidence = ({ incidenceType, incidenceDetail, companyId }: Props) => {
  const [installed, setInstalled] = React.useState<boolean>(false)
  const incidenceId = incidenceDetail.attributes.incidenceId
  const Component = selectComponent(incidenceType)
  const [authStatus, setAuthStatus] = React.useState<AuthStatus>(AuthStatus.NotAvailable)
  const [status, setStatus] = React.useState<Sync | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const isBookkeeperView = useBookkeeperView()
  const isUserAuthenticated = authStatus === AuthStatus.Authenticated
  const isHiringIncidence = incidenceType === INCIDENCE_TYPES.hiring

  const fetchAuthStatus = async () => {
    setAuthStatus(
      await authService.getStatus({
        companyId: companyId,
        bookkeeperView: isBookkeeperView,
      })
    )
  }

  const connect = async () => {
    await authService.connect(authStatus, companyId)
    fetchAuthStatus()
  }

  useEffect(() => {
    if (!installed) return
    if (Component === null) return
    if (!isUserAuthenticated && !isHiringIncidence) return

    const checkStatus = async () => {
      const incidenceStatus = await IncidenceSync.status({
        incidenceId,
        integration: PayrollIntegration.silaeApi,
        isBookkeeperView: isBookkeeperView,
      })
      setStatus(incidenceStatus)
    }
    checkStatus()
  }, [installed, Component, companyId, incidenceId, isBookkeeperView, authStatus])

  const { loading } = useAsyncData(async () => {
    const isSilaeInstalled = await CompanyInstallationsChecker.isSilaeApiInstalled()
    if (!isSilaeInstalled) return
    setInstalled(isSilaeInstalled)

    await fetchAuthStatus()
  }, [])

  const { running: syncing, performAction: onSync } = useAsyncAction(
    async () => {
      try {
        const sync = await IncidenceSync.sync({
          incidenceId: incidenceDetail.attributes.incidenceId,
          integration: PayrollIntegration.silaeApi,
          isBookkeeperView: isBookkeeperView,
        })

        setStatus(sync)
        setError(null)
      } catch (e: unknown) {
        let message: string
        if (e instanceof NetworkError) {
          message = ''
        } else if (e instanceof Error) {
          message = e.message
        } else {
          message = JSON.stringify(error)
        }

        setError(message)
        throw e
      }
    },
    {
      successMessage: i18n.t('payroll_integrations.incidences.silae_api.sync.success_message'),
    }
  )

  const show = !loading && installed && Component !== null && authStatus !== AuthStatus.NotAvailable

  if (!show) return <></>

  if (error !== null)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Errored error={error} syncing={syncing} onSync={onSync} />
      </Box>
    )

  // For Hiring incidences we want to show the status of an incidence even before the user being logged in
  if (status?.status === Status.Unavailable && !isUserAuthenticated && isHiringIncidence)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component sync={status} syncing={syncing} onSync={onSync} />
      </Box>
    )

  if (authStatus != AuthStatus.Authenticated)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <NotAuthenticated connect={connect} />
      </Box>
    )

  return (
    status && (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component sync={status} syncing={syncing} onSync={onSync} />
      </Box>
    )
  )
}

export default Incidence
